import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { supabase } from '../../../lib/supabase';

interface UploadSectionProps {
  onUploadComplete: () => void;
  selectedFolder?: string;
  selectedSubfolder?: string;
}

export default function UploadSection({ onUploadComplete, selectedFolder, selectedSubfolder }: UploadSectionProps) {
  const { isAdmin } = useAuth();
  const [files, setFiles] = useState<File[]>([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [folder, setFolder] = useState(selectedFolder || '');
  const [subfolder, setSubfolder] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: number}>({});
  const [availableFolders, setAvailableFolders] = useState<string[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch available folders from Supabase storage
  useEffect(() => {
    const fetchFolders = async () => {
      try {
        setLoadingFolders(true);
        
        const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
        const response = await fetch(
          `${supabaseUrl}/functions/v1/get-resources-public`
        );
        
        if (!response.ok) {
          console.error('Error fetching resources');
          return;
        }

        const data = await response.json();
        
        if (data.success && data.resources) {
          const uniqueFolders = new Set<string>();
          data.resources.forEach((resource: any) => {
            if (resource.folder) {
              uniqueFolders.add(resource.folder);
            }
          });
          
          const folderArray = Array.from(uniqueFolders).sort();
          setAvailableFolders(folderArray);
        }
      } catch (err) {
        console.error('Error loading folders:', err);
      } finally {
        setLoadingFolders(false);
      }
    };

    fetchFolders();
  }, []);

  // Update folder when selectedFolder changes
  useEffect(() => {
    if (selectedFolder) {
      setFolder(selectedFolder);
    }
  }, [selectedFolder]);

  // Update subfolder when selectedSubfolder changes
  useEffect(() => {
    if (selectedSubfolder !== undefined) {
      setSubfolder(selectedSubfolder);
    }
  }, [selectedSubfolder]);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files) {
      const newFiles = Array.from(e.dataTransfer.files).slice(0, 10);
      setFiles(newFiles);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files).slice(0, 10);
      setFiles(newFiles);
    }
  };

  const removeFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (files.length === 0) {
      setError('Please select at least one file to upload');
      return;
    }

    if (files.length > 10) {
      setError('Maximum 10 files allowed per upload');
      return;
    }

    if (!folder) {
      setError('Please select a folder');
      return;
    }

    try {
      setUploading(true);
      setError('');
      setSuccess(false);
      setUploadProgress({});

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('You must be logged in as admin to upload');
        setUploading(false);
        return;
      }

      const uploadSubfolder = selectedSubfolder || subfolder;

      const uploadPromises = files.map(async (file, index) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('title', title.trim() || file.name.replace(/\.[^/.]+$/, ''));
        formData.append('description', description.trim());
        formData.append('folder', folder);
        if (uploadSubfolder) {
          formData.append('subfolder', uploadSubfolder);
        }

        setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));

        const response = await fetch(
          `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/upload-resource-public`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: formData,
          }
        );

        const responseData = await response.json();

        if (!response.ok) {
          throw new Error(`${file.name}: ${responseData.error || responseData.message || 'Upload failed'}`);
        }

        if (!responseData.success) {
          throw new Error(`${file.name}: ${responseData.error || responseData.message || 'Upload unsuccessful'}`);
        }

        setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));

        return responseData;
      });

      await Promise.all(uploadPromises);

      setFiles([]);
      setTitle('');
      setDescription('');
      if (!selectedSubfolder) {
        setSubfolder('');
      }
      setSuccess(true);
      setUploadProgress({});
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      onUploadComplete();
      setTimeout(() => setSuccess(false), 3000);

    } catch (error: any) {
      console.error('Upload error:', error);
      setError(error?.message || 'Failed to upload files. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
          <i className="ri-upload-cloud-2-line text-xl text-blue-600"></i>
        </div>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Upload Resources</h2>
          <p className="text-sm text-gray-600">Add up to 10 learning materials at once</p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
          <i className="ri-error-warning-line mr-2"></i>
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-100 border border-green-300 text-green-700 rounded-lg text-sm">
          <i className="ri-check-circle-line mr-2"></i>
          Files uploaded successfully!
        </div>
      )}

      <form onSubmit={handleUpload} className="space-y-6">
        {/* File Upload Area */}
        <div
          className={`border-3 border-dashed rounded-2xl p-12 text-center transition-all duration-300 cursor-pointer ${
            dragActive
              ? 'border-teal-500 bg-teal-50 scale-105'
              : success
              ? 'border-green-500 bg-green-50'
              : error
              ? 'border-red-500 bg-red-50'
              : 'border-gray-300 bg-gray-50 hover:border-teal-400 hover:bg-teal-50'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,video/*,.mp4,.webm,.avi,.mov,.gif"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          {files.length > 0 ? (
            <div className="space-y-3">
              <div className="flex items-center justify-center gap-2 mb-4">
                <i className="ri-file-list-3-line text-2xl text-green-600"></i>
                <span className="font-medium text-gray-900">{files.length} file{files.length > 1 ? 's' : ''} selected</span>
              </div>
              
              <div className="max-h-40 overflow-y-auto space-y-2">
                {files.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center gap-3">
                      <i className="ri-file-line text-lg text-blue-600"></i>
                      <div className="text-left">
                        <p className="font-medium text-gray-900 text-sm">{file.name}</p>
                        <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                    </div>
                    {uploading && uploadProgress[file.name] !== undefined ? (
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress[file.name]}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-600">{uploadProgress[file.name]}%</span>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                        className="text-red-500 hover:text-red-700 cursor-pointer"
                        disabled={uploading}
                      >
                        <i className="ri-close-line text-lg"></i>
                      </button>
                    )}
                  </div>
                ))}
              </div>
              
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium cursor-pointer"
                disabled={uploading || files.length >= 10}
              >
                <i className="ri-add-line mr-1"></i>
                {files.length >= 10 ? 'Maximum 10 files' : 'Add more files'}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="w-16 h-16 mx-auto bg-teal-100 rounded-full flex items-center justify-center">
                <i className="ri-upload-cloud-2-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-800">Drop files here or click to browse</h3>
              <p className="text-gray-600">Supports PDF documents, videos, and animations</p>
              <div className="flex justify-center space-x-6 text-sm text-gray-500">
                <span className="flex items-center">
                  <i className="ri-file-pdf-line mr-1"></i>
                  PDF
                </span>
                <span className="flex items-center">
                  <i className="ri-video-line mr-1"></i>
                  Videos
                </span>
                <span className="flex items-center">
                  <i className="ri-image-line mr-1"></i>
                  Animations
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500 mb-4">
            Supported formats: PDF, MP4, WebM, AVI, MOV, GIF • Maximum file size: 500MB
          </p>
        </div>

        {/* Form Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Title (optional)
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="Leave blank to use file names"
            />
            <p className="text-xs text-gray-500 mt-1">If empty, each file will use its original name</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Folder *
            </label>
            {loadingFolders ? (
              <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm text-gray-500 flex items-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>
                Loading folders...
              </div>
            ) : (
              <select
                value={folder}
                onChange={(e) => {
                  setFolder(e.target.value);
                  setSubfolder('');
                }}
                className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                required
              >
                <option value="">Select a folder</option>
                {availableFolders.map((folderName) => (
                  <option key={folderName} value={folderName}>
                    {folderName}
                  </option>
                ))}
              </select>
            )}
            {availableFolders.length === 0 && !loadingFolders && (
              <p className="text-xs text-gray-500 mt-1">No folders available. Create a folder first.</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Subfolder Path {selectedSubfolder && '(Current Location)'}
          </label>
          <input
            type="text"
            value={subfolder}
            onChange={(e) => setSubfolder(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            placeholder={selectedSubfolder ? `Currently in: ${selectedSubfolder}` : "e.g., Chapter 1/Lesson 1 or Algebra/Quadratics"}
            disabled={!!selectedSubfolder}
          />
          <p className="text-xs text-gray-500 mt-1">
            {selectedSubfolder 
              ? `Files will be uploaded to: ${selectedSubfolder}` 
              : 'Use / to create nested subfolders (e.g., "Chapter 1/Lesson 1/Exercises")'
            }
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description (optional)
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
            placeholder="Add a brief description for all files (optional)"
          />
        </div>

        <button
          type="submit"
          disabled={uploading || files.length === 0 || !folder}
          className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
        >
          {uploading ? (
            <span className="flex items-center justify-center">
              <i className="ri-loader-4-line animate-spin mr-2"></i>
              Uploading {files.length} file{files.length > 1 ? 's' : ''}...
            </span>
          ) : (
            <span className="flex items-center justify-center">
              <i className="ri-upload-2-line mr-2"></i>
              Upload {files.length > 0 ? `${files.length} ` : ''}Resource{files.length > 1 ? 's' : ''}
            </span>
          )}
        </button>
      </form>
    </div>
  );
}
